import { useState, useEffect } from "react";
import { Car, Motorcycle, Bicycle, Bus, MapPin, User, Buildings, Bluetooth, Plus, Trash, PencilSimple, CaretDown, Package, Lightning, Crosshair } from "@phosphor-icons/react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VEHICLE_ICONS = { van: Car, motorcycle: Motorcycle, bicycle: Bicycle, bus: Bus, train: Bus };
const VEHICLE_LABELS = { van: "Carrinha", motorcycle: "Mota", bicycle: "Bicicleta", bus: "Autocarro", train: "Comboio" };
const STATUS_LABELS = { active: "Ativo", en_route: "Em rota", available: "Disponivel", maintenance: "Manutencao", inactive: "Inativo" };
const STATUS_COLORS = { active: "var(--success)", en_route: "#2563eb", available: "var(--warning)", maintenance: "var(--accent)", inactive: "var(--text-muted)" };
const COMPANY_TYPE_LABELS = { transport: "Transporte", courier: "Estafeta", partner: "Parceiro" };

function VehicleCard({ v, onEdit, onDelete, onSimulate }) {
  const Icon = VEHICLE_ICONS[v.vehicle_type] || Car;
  const sc = STATUS_COLORS[v.status] || "var(--text-muted)";
  return (
    <div className="bento-item p-5 card-hover" data-testid={`vehicle-card-${v.plate}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div style={{ width: 44, height: 44, borderRadius: 12, background: `${sc}15`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon size={22} weight="duotone" style={{ color: sc }} />
          </div>
          <div>
            <span style={{ fontFamily: "JetBrains Mono", fontWeight: 700, fontSize: "1rem", display: "block" }}>{v.plate}</span>
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{v.brand} {v.model}</span>
          </div>
        </div>
        <span style={{ padding: "3px 10px", borderRadius: 9999, fontSize: "0.7rem", fontWeight: 600, background: `${sc}15`, color: sc }}>
          {STATUS_LABELS[v.status] || v.status}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div style={{ padding: 8, borderRadius: 8, background: "var(--bg)", fontSize: "0.75rem" }}>
          <span style={{ color: "var(--text-muted)", display: "block", fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase" }}>Motorista</span>
          <span style={{ fontWeight: 600 }}>{v.driver_name}</span>
        </div>
        <div style={{ padding: 8, borderRadius: 8, background: "var(--bg)", fontSize: "0.75rem" }}>
          <span style={{ color: "var(--text-muted)", display: "block", fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase" }}>Empresa</span>
          <span style={{ fontWeight: 600 }}>{v.company_name}</span>
        </div>
      </div>
      {v.bags_on_board && v.bags_on_board.length > 0 && (
        <div style={{ padding: 8, borderRadius: 8, background: "rgba(37,99,235,0.04)", marginBottom: 8 }}>
          <span style={{ fontSize: "0.6rem", fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", display: "block", marginBottom: 4 }}>Bolsas a bordo ({v.bags_on_board.length})</span>
          <div className="flex flex-wrap gap-1">
            {v.bags_on_board.map(b => (
              <span key={b} style={{ padding: "2px 8px", borderRadius: 9999, background: "rgba(37,99,235,0.08)", fontFamily: "JetBrains Mono", fontSize: "0.7rem", fontWeight: 600, color: "#2563eb" }}>
                {b}
              </span>
            ))}
          </div>
        </div>
      )}
      <div style={{ fontSize: "0.65rem", color: "var(--text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
        <Crosshair size={12} /> Tracker: {v.tracker_device_id || "---"}
        {v.current_lat && <span style={{ marginLeft: "auto" }}>({v.current_lat?.toFixed(3)}, {v.current_lng?.toFixed(3)})</span>}
      </div>
      <div className="flex gap-2 mt-3">
        <button data-testid={`edit-vehicle-${v.plate}`} onClick={() => onEdit(v)} style={{ flex: 1, padding: "6px", borderRadius: 8, border: "1px solid var(--border-color)", background: "transparent", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
          <PencilSimple size={12} /> Editar
        </button>
        <button data-testid={`delete-vehicle-${v.plate}`} onClick={() => onDelete(v.id)} style={{ padding: "6px 12px", borderRadius: 8, border: "none", background: "rgba(184,74,74,0.08)", color: "var(--error)", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600 }}>
          <Trash size={12} />
        </button>
      </div>
    </div>
  );
}

export default function VehicleManager() {
  const [activeTab, setActiveTab] = useState("vehicles");
  const [vehicles, setVehicles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [form, setForm] = useState({ plate: "", vehicle_type: "van", brand: "", model: "", company_id: "", driver_id: "", tracker_device_id: "" });
  const [companyForm, setCompanyForm] = useState({ name: "", company_type: "courier", contact_email: "", contact_phone: "", city: "" });
  const [showCompanyForm, setShowCompanyForm] = useState(false);

  const fetchAll = async () => {
    try {
      const [vRes, cRes, dRes] = await Promise.all([
        axios.get(`${API}/vehicles/`),
        axios.get(`${API}/fleet/companies`),
        axios.get(`${API}/drivers`),
      ]);
      setVehicles(vRes.data);
      setCompanies(cRes.data);
      setDrivers(dRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSaveVehicle = async () => {
    try {
      if (editingVehicle) {
        await axios.put(`${API}/vehicles/${editingVehicle.id}`, form);
      } else {
        await axios.post(`${API}/vehicles/`, form);
      }
      setShowForm(false);
      setEditingVehicle(null);
      setForm({ plate: "", vehicle_type: "van", brand: "", model: "", company_id: "", driver_id: "", tracker_device_id: "" });
      await fetchAll();
    } catch (e) { console.error(e); }
  };

  const handleDeleteVehicle = async (id) => {
    if (!window.confirm("Eliminar este veiculo?")) return;
    await axios.delete(`${API}/vehicles/${id}`);
    await fetchAll();
  };

  const handleEdit = (v) => {
    setForm({ plate: v.plate, vehicle_type: v.vehicle_type, brand: v.brand || "", model: v.model || "", company_id: v.company_id || "", driver_id: v.driver_id || "", tracker_device_id: v.tracker_device_id || "" });
    setEditingVehicle(v);
    setShowForm(true);
  };

  const handleSimulate = async () => {
    await axios.post(`${API}/vehicles/simulate`);
    await fetchAll();
  };

  const handleSaveCompany = async () => {
    await axios.post(`${API}/fleet/companies`, companyForm);
    setShowCompanyForm(false);
    setCompanyForm({ name: "", company_type: "courier", contact_email: "", contact_phone: "", city: "" });
    await fetchAll();
  };

  const handleDeleteCompany = async (id) => {
    if (!window.confirm("Eliminar esta empresa?")) return;
    await axios.delete(`${API}/fleet/companies/${id}`);
    await fetchAll();
  };

  const inputStyle = { width: "100%", padding: "10px 14px", borderRadius: 10, border: "1px solid var(--border-color)", fontFamily: "Manrope", fontSize: "0.85rem", background: "var(--bg)", outline: "none" };
  const labelStyle = { display: "block", fontSize: "0.65rem", fontWeight: 600, color: "var(--text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" };

  if (loading) return <div className="pt-20 px-6 text-center"><p style={{ color: "var(--text-muted)" }}>A carregar frota...</p></div>;

  return (
    <div data-testid="vehicle-manager" className="pt-20 px-6 pb-12">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6 animate-fade-in-up">
          <div>
            <h1 style={{ fontFamily: "Outfit", fontWeight: 700, fontSize: "1.6rem" }}>Gestor de frota</h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>Veiculos, empresas parceiras e rastreamento GPS</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="simulate-vehicles-btn" onClick={handleSimulate} style={{ padding: "8px 16px", borderRadius: 9999, border: "1px solid var(--border-color)", background: "transparent", cursor: "pointer", fontSize: "0.8rem", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
              <Lightning size={14} weight="fill" style={{ color: "var(--warning)" }} /> Simular
            </button>
            <button data-testid="add-vehicle-btn" onClick={() => { setShowForm(true); setEditingVehicle(null); setForm({ plate: "", vehicle_type: "van", brand: "", model: "", company_id: "", driver_id: "", tracker_device_id: "" }); }} style={{ padding: "8px 20px", borderRadius: 9999, border: "none", background: "var(--primary)", color: "white", cursor: "pointer", fontSize: "0.8rem", fontFamily: "Outfit", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
              <Plus size={14} weight="bold" /> Veiculo
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6" data-testid="fleet-tabs">
          {[
            { id: "vehicles", label: `Veiculos (${vehicles.length})`, icon: Car },
            { id: "companies", label: `Empresas (${companies.length})`, icon: Buildings },
          ].map(t => (
            <button key={t.id} data-testid={`fleet-tab-${t.id}`} onClick={() => setActiveTab(t.id)} className="flex items-center gap-2" style={{ padding: "8px 16px", borderRadius: 9999, border: "none", background: activeTab === t.id ? "var(--primary)" : "var(--surface)", color: activeTab === t.id ? "white" : "var(--text-muted)", fontFamily: "Outfit", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer", boxShadow: activeTab !== t.id ? "inset 0 0 0 1px var(--border-color)" : "none" }}>
              <t.icon size={14} weight={activeTab === t.id ? "fill" : "regular"} /> {t.label}
            </button>
          ))}
        </div>

        {/* Vehicle form modal */}
        {showForm && (
          <div className="bento-item p-6 mb-6 animate-fade-in" data-testid="vehicle-form">
            <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem", marginBottom: 16 }}>{editingVehicle ? "Editar veiculo" : "Novo veiculo"}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div><label style={labelStyle}>Matricula</label><input data-testid="vehicle-plate-input" style={inputStyle} value={form.plate} onChange={e => setForm({...form, plate: e.target.value})} placeholder="AA-01-BM" /></div>
              <div><label style={labelStyle}>Tipo</label>
                <select data-testid="vehicle-type-select" style={{...inputStyle, appearance: "none"}} value={form.vehicle_type} onChange={e => setForm({...form, vehicle_type: e.target.value})}>
                  {Object.entries(VEHICLE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Marca</label><input style={inputStyle} value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} placeholder="Mercedes" /></div>
              <div><label style={labelStyle}>Modelo</label><input style={inputStyle} value={form.model} onChange={e => setForm({...form, model: e.target.value})} placeholder="Sprinter" /></div>
              <div><label style={labelStyle}>Empresa</label>
                <select style={{...inputStyle, appearance: "none"}} value={form.company_id} onChange={e => setForm({...form, company_id: e.target.value})}>
                  <option value="">Sem empresa</option>
                  {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Motorista/Estafeta</label>
                <select style={{...inputStyle, appearance: "none"}} value={form.driver_id} onChange={e => setForm({...form, driver_id: e.target.value})}>
                  <option value="">Sem motorista</option>
                  {drivers.map(d => <option key={d.id} value={d.id}>{d.name} ({d.vehicle_type})</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Tracker ID</label><input style={inputStyle} value={form.tracker_device_id} onChange={e => setForm({...form, tracker_device_id: e.target.value})} placeholder="TRK-VAN-001" /></div>
            </div>
            <div className="flex gap-2 mt-4">
              <button data-testid="save-vehicle-btn" onClick={handleSaveVehicle} style={{ padding: "10px 24px", borderRadius: 9999, border: "none", background: "var(--primary)", color: "white", fontFamily: "Outfit", fontWeight: 600, fontSize: "0.85rem", cursor: "pointer" }}>
                {editingVehicle ? "Guardar" : "Criar veiculo"}
              </button>
              <button onClick={() => { setShowForm(false); setEditingVehicle(null); }} style={{ padding: "10px 20px", borderRadius: 9999, border: "1px solid var(--border-color)", background: "transparent", cursor: "pointer", fontSize: "0.85rem" }}>Cancelar</button>
            </div>
          </div>
        )}

        {/* Vehicles grid */}
        {activeTab === "vehicles" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-fade-in">
            {vehicles.map(v => <VehicleCard key={v.id} v={v} onEdit={handleEdit} onDelete={handleDeleteVehicle} onSimulate={handleSimulate} />)}
          </div>
        )}

        {/* Companies */}
        {activeTab === "companies" && (
          <div className="animate-fade-in">
            <button data-testid="add-company-btn" onClick={() => setShowCompanyForm(true)} className="flex items-center gap-2 mb-4" style={{ padding: "8px 20px", borderRadius: 9999, border: "none", background: "var(--primary)", color: "white", cursor: "pointer", fontSize: "0.8rem", fontFamily: "Outfit", fontWeight: 600 }}>
              <Plus size={14} weight="bold" /> Empresa parceira
            </button>
            {showCompanyForm && (
              <div className="bento-item p-6 mb-4 animate-fade-in" data-testid="company-form">
                <h3 style={{ fontFamily: "Outfit", fontWeight: 600, fontSize: "1rem", marginBottom: 12 }}>Nova empresa parceira</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div><label style={labelStyle}>Nome</label><input data-testid="company-name-input" style={inputStyle} value={companyForm.name} onChange={e => setCompanyForm({...companyForm, name: e.target.value})} /></div>
                  <div><label style={labelStyle}>Tipo</label>
                    <select style={{...inputStyle, appearance: "none"}} value={companyForm.company_type} onChange={e => setCompanyForm({...companyForm, company_type: e.target.value})}>
                      {Object.entries(COMPANY_TYPE_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div><label style={labelStyle}>Cidade</label><input style={inputStyle} value={companyForm.city} onChange={e => setCompanyForm({...companyForm, city: e.target.value})} /></div>
                  <div><label style={labelStyle}>Email</label><input style={inputStyle} value={companyForm.contact_email} onChange={e => setCompanyForm({...companyForm, contact_email: e.target.value})} /></div>
                  <div><label style={labelStyle}>Telefone</label><input style={inputStyle} value={companyForm.contact_phone} onChange={e => setCompanyForm({...companyForm, contact_phone: e.target.value})} /></div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button data-testid="save-company-btn" onClick={handleSaveCompany} style={{ padding: "8px 20px", borderRadius: 9999, border: "none", background: "var(--primary)", color: "white", fontFamily: "Outfit", fontWeight: 600, fontSize: "0.8rem", cursor: "pointer" }}>Criar empresa</button>
                  <button onClick={() => setShowCompanyForm(false)} style={{ padding: "8px 16px", borderRadius: 9999, border: "1px solid var(--border-color)", background: "transparent", cursor: "pointer", fontSize: "0.8rem" }}>Cancelar</button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {companies.map(c => (
                <div key={c.id} className="bento-item p-5 card-hover" data-testid={`company-card-${c.name}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div style={{ width: 40, height: 40, borderRadius: 12, background: "rgba(37,99,235,0.08)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Buildings size={20} weight="duotone" style={{ color: "#2563eb" }} />
                      </div>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: "0.95rem", display: "block" }}>{c.name}</span>
                        <span style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>{COMPANY_TYPE_LABELS[c.company_type] || c.company_type}</span>
                      </div>
                    </div>
                    <button onClick={() => handleDeleteCompany(c.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}>
                      <Trash size={14} />
                    </button>
                  </div>
                  <div className="flex flex-col gap-1" style={{ fontSize: "0.8rem" }}>
                    <div className="flex items-center gap-2"><MapPin size={12} style={{ color: "var(--text-muted)" }} /> {c.city || "---"}</div>
                    <div className="flex items-center gap-2"><Car size={12} style={{ color: "var(--text-muted)" }} /> {c.vehicles_count} veiculos</div>
                    {c.contact_email && <div style={{ color: "var(--text-muted)", fontSize: "0.75rem" }}>{c.contact_email}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
